import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Input,
  Spinner,
  Typography,
} from "@material-tailwind/react";
import toast from "react-hot-toast";
import ConfirmPayment from "../../../common/shared/ConfirmPayment";
import * as api from "../../../redux/api/nfc_card/nfc_card_api";

const formatMoney = (amount, currency = "GHS") => {
  return `${currency} ${Number(amount || 0).toLocaleString()}`;
};

function NfcAccountCard({ compact = false }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topupLoading, setTopupLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleAmountChange = (value) => {
    const cleaned = value.replace(/[^\d]/g, "");
    setAmount(cleaned);
  };

  const handleOpenConfirm = () => {
    const parsedAmount = Number.parseInt(amount, 10);

    if (!Number.isInteger(parsedAmount) || parsedAmount <= 0) {
      toast.error("Please enter a valid top-up amount.");
      return;
    }

    setConfirmOpen(true);
  };

  const handleTopup = async (e) => {
    e.preventDefault();
    setTopupLoading(true);

    try {
      const response = await api.topupNfcAccount({ amount });
      const { error, data } = response;

      if (error) {
        toast.error(error);
        return;
      }

      const paymentUrl = data?.topup?.url || data?.topup?.receipt?.url;

      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else {
        toast.error("Payment link could not be generated. Please try again.");
      }
    } catch (error) {
      toast.error("No Connection");
    } finally {
      setTopupLoading(false);
      setConfirmOpen(false);
    }
  };

  useEffect(() => {
    api
      .getNfcAccount()
      .then((response) => {
        if (!response.error) {
          setAccount(response.data?.account || null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return null;
  }

  if (!account?.available) {
    return null;
  }

  const content = (
    <div className="flex flex-col lg:flex-row lg:items-end gap-5 justify-between">
      <div>
        <Typography variant={compact ? "h6" : "h5"} color="black">
          NFC Account
        </Typography>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <span>Account Number</span>
            <Typography variant="h6" color="black">
              {account.account_number}
            </Typography>
          </div>
          <div>
            <span>Account Balance</span>
            <Typography variant="h6" color="black">
              {formatMoney(account.balance, account.currency)}
            </Typography>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 lg:min-w-[360px]">
        <Input
          color="black"
          label="Top-up Amount"
          value={amount}
          onChange={(e) => handleAmountChange(e.target.value)}
        />
        <Button
          color="pink"
          onClick={handleOpenConfirm}
          disabled={topupLoading}
          className="whitespace-nowrap"
        >
          {topupLoading ? <Spinner color="white" /> : "Top Up Account"}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {compact ? (
        <div className="mt-6 rounded border border-blue-gray-50 p-4">
          {content}
        </div>
      ) : (
        <Card className="mb-8">
          <CardBody>{content}</CardBody>
        </Card>
      )}

      {confirmOpen && (
        <ConfirmPayment
          open={confirmOpen}
          setOpen={setConfirmOpen}
          title="Electronic"
          amount={amount || 0}
          loading={topupLoading}
          handleConfirm={(e) => handleTopup(e)}
          handleClose={() => setConfirmOpen(false)}
        />
      )}
    </>
  );
}

export default NfcAccountCard;
